import { Modal, Button, Form } from "react-bootstrap";
import { MapContext } from "../../context/MapContext";
import { SEARCH_DATA } from "../../constants/Constant";
import { toHalfWidth } from "../../utils/Util";
import { useContext, useEffect, useState, useCallback } from "react";
import { saveState } from "../../utils/SaveStateUtil";
import {
  setIsOredrHistoryView,
  setOrderHistoryState,
} from "../../utils/MapManager";
import { useAuthStatus } from "../../context/AuthContext";
import {
  clearOrderHistoryMap,
  getOrderHistory,
} from "../../api/orderHistoryApi";
import { DataGrid } from "@mui/x-data-grid";
import { jaJP } from "@mui/x-data-grid/locales";
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from "@mui/material/styles";

// DataGridに適用するための、空のMUIテーマを作成
const defaultTheme = createTheme();

/**
 * 購入履歴を表示するモーダルコンポーネント
 * @param {object} props - コンポーネントのプロパティ
 * @param {boolean} props.show - モーダルを表示するかどうか
 * @param {function} props.handleClose - モーダルが非表示になるときに呼び出される関数
 * @param {function} props.showNiniModal - 任意座標ダイアログの表示
 */
const OrderHistoryModal = ({ show, handleClose, showNiniModal }) => {
  const {
    map,
    isCheckSaveState,
    isReflectHistoryOnMap,
    setIsReflectHistoryOnMap,
  } = useContext(MapContext);
  const { isLoading, isUserLoggedIn } = useAuthStatus();
  const [orderHistoryList, setOrderHistoryList] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  // 状態保存
  useEffect(() => {
    isCheckSaveState &&
      saveState("isReflectHistoryOnMap", isReflectHistoryOnMap);
  }, [isCheckSaveState, isReflectHistoryOnMap]);

  /**
   * 購入履歴の取得
   */
  const fetchOrderHistory = useCallback(async () => {
    if (isLoading || !map) {
      return;
    }
    if (isUserLoggedIn) {
      setIsFetching(true);
      setOrderHistoryList([]);
      try {
        setOrderHistoryList(await getOrderHistory());
      } catch (error) {
        console.error("購入履歴の取得に失敗しました:", error);
        clearOrderHistoryMap();
        setOrderHistoryList([]);
      } finally {
        setIsFetching(false);
      }
    } else {
      clearOrderHistoryMap();
      setOrderHistoryList([]);
    }
    setOrderHistoryState(map);
  }, [isLoading, map, isUserLoggedIn]);

  // 初回読み込みとログイン状態の監視
  useEffect(() => {
    fetchOrderHistory();
  }, [fetchOrderHistory]);

  const handleReflectHistoryClick = (e) => {
    setIsReflectHistoryOnMap(e.target.checked);

    // 地図に反映
    setIsOredrHistoryView(e.target.checked);
    setOrderHistoryState(map);
  };

  // 購入フラグをテキストに変換するヘルパー関数
  const getOrderFlagText = (flag) => {
    switch (flag) {
      case "3":
        return "購入完了";
      case "4":
        return "購入不可";
      default:
        return "";
    }
  };
  // 住所情報を連結して一つの文字列にする
  const getFullAddress = (item) => {
    const addressParts = [
      item.pref,
      item.city,
      item.oaza,
      item.chome,
      item.koaza,
      item.yobi,
      item.chiban,
    ].filter(Boolean); // null, undefined, 空文字列を除外

    return addressParts.join("");
  };

  const onRowClick = async (item) => {
    // 地番検索を行い、位置情報を取得する
    const searchKeyArray = [
      item.city,
      item.oaza,
      item.chome,
      item.koaza,
      item.yobi,
      toHalfWidth(item.chiban),
    ].filter(Boolean);

    const getCode = (code) => {
      return code.length >= 5 ? `${code.substr(0, 5)}/${code}` : code;
    };

    let searchResult = { code1: item.prefcode };
    let preCoodinate;
    for (let i = 0; i < searchKeyArray.length; i++) {
      preCoodinate = [searchResult.x, searchResult.y];
      let data = await fetch(
        `${SEARCH_DATA}${getCode(searchResult.code1)}.json`
      ).then((response) => response.json());

      if (data.length === 1 && data[0].chimei === "") {
        // 1件かつchimeiが空の場合、もう一度検索する
        data = await fetch(`${SEARCH_DATA}${getCode(data[0].code1)}.json`).then(
          (response) => response.json()
        );
      }

      const searchProp = i !== searchKeyArray.length - 1 ? "chimei" : "lot";
      searchResult = data.find((obj) => obj[searchProp] === searchKeyArray[i]);
      if (!searchResult) {
        break;
      }
    }

    // 地図移動・表示
    if (!searchResult) {
      alert("地番がみつかりません");
      if (preCoodinate[0] && preCoodinate[1]) {
        map.panTo(preCoodinate);
      }
    } else if (searchResult.flag === 0) {
      // 公共座標
      if (map.getZoom() < 18) {
        map.setZoom(18);
      }
      map.panTo([searchResult.x, searchResult.y]);
    } else {
      // 任意座標
      if (preCoodinate[0] && preCoodinate[1]) {
        map.panTo(preCoodinate);
      }
      showNiniModal(
        [{ FileNameELEM: searchResult.file, L_File_SIF_OAZA: [] }],
        searchResult.x,
        searchResult.y
      );
    }

    // ダイアログを閉じる
    handleClose();
  };

  // DataGrid用のカラム定義
  const columns = [
    { field: "order_dt", headerName: "購入日時", width: 180 },
    {
      field: "fullAddress",
      headerName: "地番",
      flex: 1,
      valueGetter: (value, row) => getFullAddress(row),
    },
    {
      field: "orderStatus",
      headerName: "購入状態",
      width: 120,
      valueGetter: (value, row) => getOrderFlagText(row.order_flag),
    },
  ];

  // DataGrid用の行データ生成
  const rows = orderHistoryList.map((item, index) => ({
    id: index,
    ...item,
  }));

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      scrollable
      dialogClassName="order-history-modal"
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>購入履歴</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Switch
          id="chkReflectHistory"
          className="mb-3"
          label="購入履歴を地図に反映"
          checked={isReflectHistoryOnMap}
          onChange={handleReflectHistoryClick}
        />
        <div style={{ height: "calc(100vh - 300px)", width: "100%" }}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={defaultTheme}>
              <DataGrid
                rows={rows}
                columns={columns}
                onRowClick={(params) => onRowClick(params.row)}
                localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
                sx={{
                  // ストライプ
                  "& .MuiDataGrid-row.even": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)", // 淡い灰色
                  },
                  "& .MuiDataGrid-row:hover": {
                    cursor: "pointer",
                    backgroundColor: "rgba(0, 0, 0, 0.5)", // 濃い灰色
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #dee2e6",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #dee2e6",
                  },
                  // フッターのスタイル
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: "1px solid #dee2e6",
                    "& p": {
                      margin: 0, // Bootstrapとの競合対策
                    },
                  },
                  // ボーダーを消す
                  border: 0,
                }}
                density="compact" // 行の高さを詰める
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
              />
            </ThemeProvider>
          </StyledEngineProvider>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={handleClose}>
          閉じる
        </Button>
        <div className="flex-fill" />
        <Button
          variant="primary"
          onClick={fetchOrderHistory}
          disabled={isFetching}
        >
          更新
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OrderHistoryModal;
